"use client";

import { getSession, signOut } from 'next-auth/react'
import { hasSessionError } from '@/lib/auth/session-utils-client'

// Client credential fetching for anonymous access
// Centralized API header configuration
export const getRequiredHeaders = () => ({
  'X-ESK-VERSION': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'
})

export const API_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'
const fetchClientCredentials = async (): Promise<void> => {
  let fullUrl: string;
  
  if (typeof window === 'undefined') {
    // Server-side: Hit Django backend directly
    const baseUrl = process.env.SILO_API_URL || 'http://host.docker.internal:8000';
    fullUrl = `${baseUrl}/api/auth/login/`;
  } else {
    // Client-side: Use Next.js API proxy
    fullUrl = '/api/auth/login/';
  }

  const response = await fetch(fullUrl, {
    method: 'POST',
    credentials: 'include', // This will send/receive cookies
    headers: { 
      'Content-Type': 'application/json', 
      'X-ESK-VERSION': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0' 
    },
    body: JSON.stringify({
      client_id: process.env.NEXT_PUBLIC_CLIENT_ID,
      client_secret: process.env.NEXT_PUBLIC_CLIENT_SECRET,
      grant_type: 'client_credentials',
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch client credentials')
  }
  
  // Credentials are managed via cookies with 'credentials: include'
  await response.json() // Consume response if needed
}

// User credential refresh for authenticated users
const refreshUserCredentials = async (): Promise<void> => {
  let fullUrl: string;
  
  if (typeof window === 'undefined') {
    // Server-side: Hit Django backend directly
    const baseUrl = process.env.SILO_API_URL || 'http://host.docker.internal:8000';
    fullUrl = `${baseUrl}/api/auth/jwt/token/refresh`;
  } else {
    // Client-side: Use Next.js API proxy
    fullUrl = '/api/auth/jwt/token/refresh';
  }

  const response = await fetch(fullUrl, {
    method: 'POST',
    credentials: 'include',
    headers: { 
      'Content-Type': 'application/json', 
      'X-ESK-VERSION': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'
    },
  })

  if (!response.ok) {
    throw new Error('Failed to refresh user credentials')
  }
}

// Handle unauthorized responses with appropriate credential refresh
const handleUnauthorized = async (response: Response): Promise<void> => {
  if (response.status === 401) {
    const json = await response.json()
    
    if (json.code !== 'invalid_1001') {
      // Try client credentials first for anonymous access
      await fetchClientCredentials()
    } else if (json.code === 'invalid_1001') {
      // Try user credential refresh
      await refreshUserCredentials()
    }
    throw new Error('Unauthorized')
  }
}

const MAX_RETRIES = 3

// Public endpoints that don't require authentication
// These endpoints should work even with expired sessions
const PUBLIC_ENDPOINTS = [
  '/api/crm/contact-submission/',  // Public contact form submission
  '/api/public/',                   // All public API routes
  '/api/auth/login/',               // Login endpoint
  '/api/auth/jwt/token/refresh'    // Token refresh endpoint
]

// Check if a URL is a public endpoint
const isPublicEndpoint = (url: string): boolean => {
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint))
}

// Matches catalog app's fetchx functionality with NextAuth integration
export const fetchx = async (
  url: string,
  options?: RequestInit
): Promise<Response> => {
  // Get the current session from NextAuth
  const session = await getSession()

  console.log('[fetchx] Session:', session ? {
    hasAccessToken: !!session.accessToken,
    email: session.user?.email,
    error: session.error
  } : 'No session')

  // CRITICAL FIX: Only check session errors for protected endpoints
  // Public endpoints should work even with expired sessions
  if (!isPublicEndpoint(url) && hasSessionError(session)) {
    // Sign out and redirect to login
    await signOut({ callbackUrl: '/login' })

    // Show user-friendly error message
    if (typeof window !== 'undefined') {
      const { toast } = await import('sonner')
      toast.error('Your session has expired. Please log in again.')
    }

    throw new Error('Session expired. Please log in again.')
  }
  
  // CRITICAL: Always include credentials for cookies
  const config: RequestInit = {
    ...options,
    credentials: 'include', // CRITICAL: Include cookies
    headers: {
      ...options?.headers,
      // CRITICAL: Layer 1 - API Access Header (MANDATORY for ALL requests)
      'X-ESK-VERSION': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
      // Layer 2 - Add authorization header if user is authenticated
      ...(session?.accessToken && {
        'Authorization': `Bearer ${session.accessToken}`
      })
    }
  }
  
  // CRITICAL FIX: Determine the correct base URL
  let baseUrl: string;
  let fullUrl: string;
  
  if (typeof window === 'undefined') {
    // Server-side: Hit Django backend directly
    baseUrl = process.env.SILO_API_URL || 'http://host.docker.internal:8000';
    fullUrl = `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  } else {
    // Client-side: Use Next.js API proxy (no base URL needed, just relative paths)
    fullUrl = url.startsWith('/') ? url : `/${url}`;
  }
  
  let retries = 0

  // Retry logic for 401 errors with simplified token refresh
  while (retries < MAX_RETRIES) {
    console.log(`[fetchx] Making request to: ${fullUrl}`)
    const response = await fetch(fullUrl, config)
    console.log(`[fetchx] Response status: ${response.status}`)

    // Handle token expiration with automatic refresh (following integration guide pattern)
    if (response.status === 401) {
      const errorData = await response.json().catch(() => null)
      
      // Check if it's a token expiration error
      if (errorData?.detail === 'invalid_refresh_token' || 
          errorData?.custom_message?.includes('refresh')) {
        try {
          // Attempt to refresh token using NextAuth
          const freshSession = await getSession()
          
          if (hasSessionError(freshSession)) {
            if (typeof window !== 'undefined') {
              const { toast } = await import('sonner')
              toast.error('Your session has expired. Please log in again.')
            }
            await signOut({ callbackUrl: '/login' })
            throw new Error('Session expired. Please log in again.')
          }
          
          if (freshSession?.accessToken && freshSession.accessToken !== session?.accessToken) {
            // Retry original request with fresh token
            const retryConfig: RequestInit = {
              ...config,
              headers: {
                ...config.headers,
                'Authorization': `Bearer ${freshSession.accessToken}`
              }
            }
            
            const retryResponse = await fetch(fullUrl, retryConfig)
            
            if (!retryResponse.ok && retryResponse.status === 401) {
              if (typeof window !== 'undefined') {
                const { toast } = await import('sonner')
                toast.error('Authentication failed. Please log in again.')
              }
              await signOut({ callbackUrl: '/login' })
              throw new Error('Authentication failed after token refresh')
            }
            
            return retryResponse
          }
        } catch (refreshError) {
          // Refresh failed, redirect to login
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Session expired. Please log in again.')
          }
          await signOut({ callbackUrl: '/login' })
          throw new Error('Session expired')
        }
      }
      
      // If not a refresh token error, handle as regular auth error
      retries++
      if (retries >= MAX_RETRIES) {
        if (typeof window !== 'undefined') {
          const { toast } = await import('sonner')
          toast.error('You are not authorized to access this resource.')
        }
        throw new Error('Unauthorized access')
      }
      continue
    }

    // Handle other HTTP errors with user-friendly messages
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      
      switch (response.status) {
        case 400:
          console.error('[fetchx] 400 Bad Request - Full error data:', errorData);
          console.error('[fetchx] Request URL:', fullUrl);
          console.error('[fetchx] Request body:', config.body);
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            const errorDetail = errorData?.detail || errorData?.message || errorData?.error || 'Invalid request data';
            toast.error(`Invalid request: ${errorDetail}`)
          }
          throw new Error(`Bad Request: ${JSON.stringify(errorData) || 'Invalid data provided'}`);
        
        case 403:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Access denied. You don\'t have permission to perform this action.')
          }
          throw new Error(`Forbidden: ${errorData?.message || 'Access denied'}`);
        
        case 404:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('The requested resource was not found.')
          }
          throw new Error(`Not Found: ${errorData?.message || errorData?.detail || 'Resource not found'}`);
        
        case 409:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('There was a conflict with your request. Please try again.')
          }
          throw new Error(`Conflict: ${errorData?.message || 'Request conflict'}`);
        
        case 422:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Invalid data provided. Please check your input.')
          }
          throw new Error(`Validation Error: ${errorData?.message || 'Invalid data'}`);
        
        case 429:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.warning('Too many requests. Please wait a moment and try again.')
          }
          throw new Error(`Rate Limited: ${errorData?.message || 'Too many requests'}`);
        
        case 500:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Server error. Our team has been notified. Please try again later.')
          }
          throw new Error(`Internal Server Error: ${errorData?.message || 'Server error'}`);
        
        case 502:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Service temporarily unavailable. Please try again in a few moments.')
          }
          throw new Error(`Bad Gateway: ${errorData?.message || 'Service unavailable'}`);
        
        case 503:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Service is temporarily down for maintenance. Please try again later.')
          }
          throw new Error(`Service Unavailable: ${errorData?.message || 'Service maintenance'}`);
        
        case 504:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error('Request timeout. Please try again.')
          }
          throw new Error(`Gateway Timeout: ${errorData?.message || 'Request timeout'}`);
        
        default:
          if (typeof window !== 'undefined') {
            const { toast } = await import('sonner')
            toast.error(`Something went wrong (${response.status}). Please try again.`)
          }
          throw new Error(`HTTP ${response.status}: ${errorData?.message || 'Unknown error'}`);
      }
    }
    
    // If we get here, the response was successful
    return response
  }

  if (typeof window !== 'undefined') {
    const { toast } = await import('sonner')
    toast.error('Authentication failed after multiple attempts. Please log in again.')
  }
  throw new Error('Max retries reached for 401 error')
}

// Helper to check if user is authenticated
export const isAuthenticated = async (): Promise<boolean> => {
  const session = await getSession()
  return !!session?.accessToken
}

// Helper to get current user info
export const getCurrentUser = async () => {
  const session = await getSession()
  return session?.user || null
}